import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ApiScraperLoginStatusChoices } from "@api/graphql/types-and-hooks";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

const formatTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
};

export const EhrMfaStrip: React.FC = () => {
  const {
    mfaStartedAt,
    mfaWindowMs,
    mfaAttempt,
    mfaSubmitted,
    loginStatus,
    openMfaPopover,
    openEHRLogin,
    isEHRStatusLoading,
    isOrgUsernamePassword,
  } = useEHRConnection();
  const { pathname } = useLocation();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!mfaStartedAt) {
      setTimeLeft(null);
      return;
    }
    const tick = () => setTimeLeft(Math.max(0, mfaWindowMs - (Date.now() - mfaStartedAt)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [mfaStartedAt, mfaWindowMs]);

  // Show ONLY while the login is genuinely on MFA_REQUIRED (timer running, no code
  // in flight). Gating strictly on loginStatus — not just mfaStartedAt — means
  // persisted timer state can't surface the strip while the status is still loading
  // (null), already SUCCESS, terminal (FAILED_MFA), or LOGGING_IN (code accepted →
  // "Connecting to EHR"). A wrong code re-issues MFA_REQUIRED (new 2m window) → the
  // strip reappears.
  // Org-managed (ORG_USERNAME_PASSWORD) accounts never run the interactive MFA
  // code flow, so the banner must never show for them.
  if (isOrgUsernamePassword) return null;
  if (loginStatus !== ApiScraperLoginStatusChoices.MfaRequired || !mfaStartedAt || mfaSubmitted) return null;

  const expired = timeLeft !== null && timeLeft <= 0;
  // On /onboarding the badge popover isn't mounted, so openMfaPopover is a no-op.
  // Open the login dialog directly (handleErrorsInline) so the user can still enter
  // their code after dismissing the dialog mid-MFA challenge.
  const isOnboarding = pathname === "/onboarding";

  const countdownSuffix = timeLeft === null ? "" : ` (${formatTime(timeLeft)} remaining)`;
  let messageText: string;
  if (expired) {
    messageText = "Verification window expired — reconnect your EHR to try again.";
  } else if (mfaAttempt > 1) {
    messageText = `Code not accepted — enter your verification code again${countdownSuffix}`;
  } else {
    messageText = `Your EHR needs a verification code${countdownSuffix}`;
  }
  // On an expired window there is no actionable CTA — the code can no longer be
  // entered, and "Try again" is reserved for the terminal FAILED_MFA state. The
  // message alone instructs the user to reconnect (the backend transitions to
  // FAILED_MFA, whose popover surfaces the retry).
  const handleCta = () => {
    if (isOnboarding) {
      openEHRLogin(undefined, false, true);
    } else {
      openMfaPopover();
    }
  };

  return (
    <div className="flex w-full flex-row items-center justify-between gap-2 border-b border-yellow-200 bg-yellow-50 px-6 py-2">
      <div className="flex items-center gap-2">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
        <span className="text-xs text-yellow-800">{messageText}</span>
      </div>
      {!expired && (
        <button
          type="button"
          onClick={handleCta}
          disabled={isEHRStatusLoading}
          className="shrink-0 rounded-md bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 transition-colors hover:bg-yellow-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Enter code →
        </button>
      )}
    </div>
  );
};
